import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  Divider,
} from '@mui/material';
import {
  Search,
  Clear,
  ArrowUpward,
  ArrowDownward,
  Refresh,
} from '@mui/icons-material';
import apiService from '../../services/apiService';
import '../styles/ContentComponents.css';

function Companies() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // List of sources that should always be flagged as Test
  const TEST_SOURCES = ['@divstechnology', '@moveo'];

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    totalCount: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  });

  // Search and sort state
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('create_date');
  const [sortOrder, setSortOrder] = useState('desc');
  // Filter: null = All, true = Test, false = Production (default)
  const [isTestFilter, setIsTestFilter] = useState(false);

  // Statistics state
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    newThisMonth: 0,
  });

  useEffect(() => {
    fetchCompanies();
  }, [
    pagination.page,
    pagination.limit,
    appliedSearchTerm,
    sortBy,
    sortOrder,
    isTestFilter,
  ]);

  const fetchCompanies = async () => {
    setLoading(true);
    setError(null);

    const params = {
      page: pagination.page,
      limit: pagination.limit,
      search: appliedSearchTerm || null,
      sort_by: sortBy,
      sort_order: sortOrder,
      is_test: isTestFilter,
    };

    const result = await apiService.fetchCompaniesPaginated(params);

    // After fetching companies from API
    if (result.success) {
      const { companies: companiesData, pagination: paginationData } =
        result.data;

      // Override is_test for companies created from test sources
      const overriddenCompanies = companiesData.map((company) => {
        // Check if company was created by any of the test sources
        const isFromTestSource = TEST_SOURCES.some((source) =>
          company.created_by?.toLowerCase().includes(source.toLowerCase())
        );
        // If it is from a test source, mark as test; otherwise keep existing value
        return {
          ...company,
          is_test: isFromTestSource ? true : company.is_test,
        };
      });

      // Update state with overridden companies
      setCompanies(overriddenCompanies);

      setPagination((prev) => ({
        ...prev,
        page: paginationData.page,
        limit: paginationData.limit,
        totalCount: paginationData.total_count,
        totalPages: paginationData.total_pages,
        hasNext: paginationData.has_next,
        hasPrev: paginationData.has_prev,
      }));

      // Calculate statistics from current page data
      // Note: For accurate stats, we'd need a separate endpoint for statistics
      const active = companiesData.filter((company) => {
        if (!company.subscription_start_date) return false;
        if (!company.subscription_end_date && company.subscription_start_date)
          return true;
        const endDate = new Date(company.subscription_end_date);
        const now = new Date();
        return endDate > now;
      }).length;

      const currentMonth = new Date().getMonth();
      const currentYear = new Date().getFullYear();
      const newThisMonth = companiesData.filter((company) => {
        if (!company.create_date) return false;
        const createDate = new Date(company.create_date);
        return (
          createDate.getMonth() === currentMonth &&
          createDate.getFullYear() === currentYear
        );
      }).length;

      setStats({
        total: paginationData.total_count,
        active,
        newThisMonth,
      });
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  const handleRetry = () => {
    fetchCompanies();
  };

  const handleRowClick = (company) => {
    navigate(`/company/${company.unique_id}`);
  };

  // Pagination handlers
  const handlePageChange = (event, newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleLimitChange = (event) => {
    const newLimit = parseInt(event.target.value);
    setPagination((prev) => ({ ...prev, limit: newLimit, page: 1 }));
  };

  // Search handlers
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const applySearch = () => {
    setAppliedSearchTerm(searchTerm);
    setPagination((prev) => ({ ...prev, page: 1 })); // Reset to first page when searching
  };

  const clearSearch = () => {
    setSearchTerm('');
    setAppliedSearchTerm('');
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleSearchKeyPress = (event) => {
    if (event.key === 'Enter') {
      applySearch();
    }
  };

  // Sort handlers
  const handleSortChange = (field) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setPagination((prev) => ({ ...prev, page: 1 })); // Reset to first page when sorting
  };

  // Filter handlers
  const handleTestFilterChange = (event) => {
    const value = event.target.value;
    setIsTestFilter(value === 'all' ? null : value === 'test');
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const getSubscriptionStatus = (company) => {
    if (!company.subscription_start_date) return 'No subscription';
    if (!company.subscription_end_date && company.subscription_start_date)
      return 'Free plan';

    const endDate = new Date(company.subscription_end_date);
    const now = new Date();

    if (endDate < now) return 'Expired';
    if (endDate.getTime() - now.getTime() < 7 * 24 * 60 * 60 * 1000)
      return 'Expiring Soon';
    return 'Active';
  };

  // Define table columns
  const columns = [
    {
      key: 'unique_id',
      label: 'Company ID',
      type: 'truncate',
      maxLength: 20,
    },
    {
      key: 'name',
      label: 'Name',
    },
    {
      key: 'subscription_plan_id',
      label: 'Subscription Plan',
      render: (value) => value || 'No plan',
    },
    {
      key: 'subscription_end_date',
      label: 'Subscription Status',
      render: (value, item) => {
        const status = getSubscriptionStatus(item);
        const color =
          status === 'Active'
            ? 'success'
            : status === 'Expiring Soon'
            ? 'warning'
            : 'error';
        return (
          <Chip label={status} color={color} size='small' variant='outlined' />
        );
      },
    },
    {
      key: 'is_test',
      label: 'Type',
      render: (value) => (
        <Chip
          label={value ? 'Test' : 'Production'}
          color={value ? 'warning' : 'success'}
          size='small'
          variant='outlined'
        />
      ),
    },
    {
      key: 'create_date',
      label: 'Created Date',
      type: 'date',
      render: (value) => (value ? apiService.formatDate(value) : 'N/A'),
    },
  ];

  // Render cell content based on column type
  const renderCell = (item, column) => {
    const value = item[column.key];

    if (column.render) {
      return column.render(value, item);
    }

    if (column.type === 'date') {
      return value ? new Date(value).toLocaleDateString() : 'N/A';
    }

    if (
      column.type === 'truncate' &&
      value &&
      value.length > column.maxLength
    ) {
      return <span title={value}>{value.slice(0, column.maxLength)}...</span>;
    }

    return value || 'N/A';
  };

  // Loading skeleton for table rows
  const TableSkeleton = () => (
    <>
      {Array.from({ length: pagination.limit }).map((_, index) => (
        <TableRow key={index}>
          {columns.map((column) => (
            <TableCell key={column.key} sx={{ py: 0.5 }}>
              <Skeleton variant='text' width='80%' height={16} />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );

  return (
    <Box
      sx={{
        height: '100vh',
        maxHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        bgcolor: 'grey.50',
      }}
    >
      {/* Header Section */}
      <Paper elevation={1} sx={{ p: 1.5, borderRadius: 0, flexShrink: 0 }}>
        <Box sx={{ mb: 1 }}>
          <Typography
            variant='h5'
            sx={{ fontWeight: 600, color: 'text.primary' }}
          >
            🏢 Companies Management
          </Typography>
        </Box>

        {/* Search and Controls */}
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            alignItems: 'center',
            flexWrap: 'wrap',
            mb: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              flex: 1,
              minWidth: 300,
            }}
          >
            <TextField
              fullWidth
              size='small'
              variant='outlined'
              placeholder='Search companies by name...'
              value={searchTerm}
              onChange={handleSearchChange}
              onKeyPress={handleSearchKeyPress}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <Search color='action' />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position='end'>
                    <Tooltip title='Clear search'>
                      <IconButton size='small' onClick={clearSearch}>
                        <Clear />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant='contained'
              onClick={applySearch}
              disabled={loading}
              size='small'
              sx={{ minWidth: 80 }}
            >
              Search
            </Button>
          </Box>

          <FormControl size='small' sx={{ minWidth: 120 }}>
            <InputLabel>Sort By</InputLabel>
            <Select
              value={sortBy}
              label='Sort By'
              onChange={(e) => {
                setSortBy(e.target.value);
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
            >
              <MenuItem value='create_date'>Created Date</MenuItem>
              <MenuItem value='name'>Name</MenuItem>
              <MenuItem value='subscription_plan_id'>
                Subscription Plan
              </MenuItem>
              <MenuItem value='is_test'>Type</MenuItem>
            </Select>
          </FormControl>

          <IconButton
            onClick={() => handleSortChange(sortBy)}
            color='primary'
            size='small'
            title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
          >
            {sortOrder === 'asc' ? <ArrowUpward /> : <ArrowDownward />}
          </IconButton>

          <FormControl size='small' sx={{ minWidth: 100 }}>
            <InputLabel>Filter</InputLabel>
            <Select
              value={
                isTestFilter === null
                  ? 'all'
                  : isTestFilter
                  ? 'test'
                  : 'non-test'
              }
              label='Filter'
              onChange={handleTestFilterChange}
            >
              <MenuItem value='all'>All</MenuItem>
              <MenuItem value='test'>Test</MenuItem>
              <MenuItem value='non-test'>Production</MenuItem>
            </Select>
          </FormControl>

          <FormControl size='small' sx={{ minWidth: 100 }}>
            <InputLabel>Per Page</InputLabel>
            <Select
              value={pagination.limit}
              label='Per Page'
              onChange={handleLimitChange}
            >
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Results info */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant='body2' color='text.secondary'>
            Showing {companies.length} of {pagination.totalCount} companies
            {appliedSearchTerm && ` for "${appliedSearchTerm}"`}
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Sorted by: {sortBy.replace('_', ' ')} ({sortOrder})
          </Typography>
        </Box>
      </Paper>

      {/* Error State */}
      {error && (
        <Box sx={{ p: 1, flexShrink: 0 }}>
          <Alert
            severity='error'
            action={
              <Button color='inherit' size='small' onClick={handleRetry}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        </Box>
      )}

      {/* Table Section */}
      <Box
        sx={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
      >
        <TableContainer
          component={Paper}
          sx={{
            height: 'calc(100vh - 290px)', // Reduced height to show fewer rows
            borderRadius: 0,
            overflow: 'auto',
            '& .MuiTable-root': {
              tableLayout: 'fixed',
            },
          }}
        >
          <Table stickyHeader size='small'>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.key}
                    sx={{
                      fontWeight: 600,
                      bgcolor: 'grey.100',
                      borderBottom: '2px solid',
                      borderBottomColor: 'divider',
                      py: 1,
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableSkeleton />
              ) : companies.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    align='center'
                    sx={{ py: 4 }}
                  >
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography
                        variant='h6'
                        color='text.secondary'
                        gutterBottom
                      >
                        No companies found
                      </Typography>
                      <Typography variant='body2' color='text.secondary'>
                        {appliedSearchTerm
                          ? `No results found for "${appliedSearchTerm}"`
                          : 'Try adjusting your search criteria'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                companies.map((company, index) => (
                  <TableRow
                    key={company.unique_id || index}
                    hover
                    onClick={() => handleRowClick(company)}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                      '&:nth-of-type(even)': {
                        backgroundColor: 'grey.25',
                      },
                    }}
                  >
                    {columns.map((column) => (
                      <TableCell
                        key={column.key}
                        sx={{
                          borderBottom: '1px solid',
                          borderBottomColor: 'divider',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          py: 0.5,
                        }}
                      >
                        {renderCell(company, column)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <Paper elevation={1} sx={{ p: 1, borderRadius: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Pagination
                count={pagination.totalPages}
                page={pagination.page}
                onChange={handlePageChange}
                color='primary'
                size='small'
                showFirstButton
                showLastButton
                disabled={loading}
              />
            </Box>
          </Paper>
        )}
      </Box>
    </Box>
  );
}

export default Companies;
